import { invoke } from '@tauri-apps/api/core';

import type { GdtConfig } from '../types';
import {
  GDT_COMMAND_PREFIX as COMMAND_PREFIX,
  createGdtApiCommandError,
} from './constants';

/**
 * Validation result for GDT configuration
 */
export interface ValidationResult {
  /** Whether configuration is valid */
  isValid: boolean;
  /** Errors that prevent GDT from working */
  errors: string[];
  /** Non-blocking warnings */
  warnings: string[];
}

/**
 * Result of connection test
 */
export interface TestConnectionResult {
  /** Whether test was successful */
  success: boolean;
  /** Human-readable message */
  message: string;
  /** Input/output folder accessibility and write permission */
  folderOk: boolean;
}

/**
 * Known PVS system info for dropdown
 */
export interface PvsSystemInfo {
  name: string;
  gdtId: string;
}

/**
 * Load GDT configuration from disk
 */
export async function gdtGetConfig(): Promise<GdtConfig> {
  try {
    return await invoke<GdtConfig>(`${COMMAND_PREFIX}get_gdt_config`);
  } catch (error) {
    throw createGdtApiCommandError('Failed to load GDT config', error);
  }
}

/**
 * Save GDT configuration to disk
 */
export async function gdtSaveConfig(config: GdtConfig): Promise<void> {
  try {
    await invoke<void>(`${COMMAND_PREFIX}save_gdt_config`, { config });
  } catch (error) {
    throw createGdtApiCommandError('Failed to save GDT config', error);
  }
}

/**
 * Validate GDT configuration
 */
export async function gdtValidateConfig(config: GdtConfig): Promise<ValidationResult> {
  try {
    return await invoke<ValidationResult>(`${COMMAND_PREFIX}validate_gdt_config`, {
      config,
    });
  } catch (error) {
    throw createGdtApiCommandError('Failed to validate GDT config', error);
  }
}

/**
 * Test GDT connection (folder accessibility)
 */
export async function gdtTestConnection(
  config: GdtConfig
): Promise<TestConnectionResult> {
  try {
    const result = await invoke<TestConnectionResult>(
      `${COMMAND_PREFIX}test_gdt_connection`,
      {
        config,
      }
    );
    // Normalise at the IPC boundary: invoke<T> is an unchecked cast, so enforce
    // that message is always a string even if the Tauri serialisation returns
    // something unexpected (e.g. a nested object on a malformed response).
    return {
      ...result,
      message: typeof result.message === 'string' ? result.message : '',
    };
  } catch (error) {
    throw createGdtApiCommandError('Failed to test GDT connection', error);
  }
}

/**
 * Get list of known PVS systems
 */
export async function gdtGetPvsSystems(): Promise<PvsSystemInfo[]> {
  try {
    return await invoke<PvsSystemInfo[]>(`${COMMAND_PREFIX}get_pvs_systems`);
  } catch (error) {
    throw createGdtApiCommandError('Failed to get PVS systems', error);
  }
}
